/**
 * StaffInventory — FRD §35
 * Allows staff to check-in / check-out inventory items they are responsible for.
 */
import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Button, Chip, TextField, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Grid
} from "@mui/material";
import InventoryIcon from "@mui/icons-material/Inventory2Outlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LogoutIcon from "@mui/icons-material/Logout";
import { fetchData, postData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";
import DynamicTable from "../../DynamicComponent/DynamicTable";

const FALLBACK_ITEMS = [
  { id: 1, itemName: "Laptop",        itemCode: "LAP-001", category: "Electronics",  status: "Available",  quantity: 1, location: "IT Room" },
  { id: 2, itemName: "Projector",     itemCode: "PRJ-002", category: "Electronics",  status: "Checked Out",quantity: 1, location: "Class A" },
  { id: 3, itemName: "Whiteboard",    itemCode: "WBD-003", category: "Classroom",    status: "Available",  quantity: 2, location: "Storage" },
  { id: 4, itemName: "Printer",       itemCode: "PRT-004", category: "Electronics",  status: "Available",  quantity: 1, location: "Office" },
  { id: 5, itemName: "Chair (set)",   itemCode: "CHR-005", category: "Furniture",    status: "Available",  quantity: 10,location: "Hall" },
];

const StatusChip = ({ status }) => {
  const isOut = status?.toLowerCase() === "checked out";
  return (
    <Chip
      label={status || "Unknown"}
      size="small"
      sx={{
        backgroundColor: isOut ? "#fce4ec" : "#e8f5e9",
        color: isOut ? "#c62828" : "#2e7d32",
        fontWeight: 600, fontSize: "12px",
      }}
    />
  );
};

const StaffInventory = () => {
  const [items, setItems]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast]   = useState({ open: false, severity: "success", message: "" });
  const [dialog, setDialog] = useState({ open: false, item: null, action: "" });
  const [note, setNote]     = useState("");
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  const staffId = sessionStorage.getItem("userId") || sessionStorage.getItem("applicationId") || "";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchData(`staff/inventory?staffId=${staffId}`);
      if (res?.data && Array.isArray(res.data)) {
        setItems(res.data);
      } else {
        setItems(FALLBACK_ITEMS);
      }
    } catch {
      setItems(FALLBACK_ITEMS);
    }
    setLoading(false);
  }, [staffId]);

  useEffect(() => { load(); }, [load]);

  const openDialog = (item, action) => {
    setDialog({ open: true, item, action });
    setNote("");
  };

  const handleAction = async () => {
    const { item, action } = dialog;
    setSaving(true);
    try {
      await postData("staff/inventory/transaction", {
        itemId: item.id,
        itemCode: item.itemCode,
        action,          // "check-in" or "check-out"
        staffId,
        note,
        timestamp: new Date().toISOString(),
      });
      setToast({ open: true, severity: "success", message: `${action === "check-in" ? "Checked in" : "Checked out"} successfully` });
      setDialog({ open: false, item: null, action: "" });
      load();
    } catch {
      // Optimistic update
      setItems(prev => prev.map(i =>
        i.id === item.id
          ? { ...i, status: action === "check-in" ? "Available" : "Checked Out" }
          : i
      ));
      setToast({ open: true, severity: "success", message: `${action === "check-in" ? "Check-in" : "Check-out"} recorded locally` });
      setDialog({ open: false, item: null, action: "" });
    }
    setSaving(false);
  };

  const filteredItems = items.filter(i =>
    !search || i.itemName?.toLowerCase().includes(search.toLowerCase()) ||
    i.itemCode?.toLowerCase().includes(search.toLowerCase()) ||
    i.category?.toLowerCase().includes(search.toLowerCase())
  );

  const columns = [
    { label: "Item Name",  field: "itemName" },
    { label: "Code",       field: "itemCode" },
    { label: "Category",   field: "category" },
    { label: "Qty",        field: "quantity" },
    { label: "Location",   field: "location" },
    { label: "Status",     field: "statusCell" },
    { label: "Actions",    field: "actionsCell" },
  ];

  const rows = filteredItems.map(item => ({
    ...item,
    statusCell: <StatusChip status={item.status} />,
    actionsCell: (
      <Box sx={{ display: "flex", gap: 1 }}>
        <Button
          size="small"
          startIcon={<CheckCircleOutlineIcon />}
          disabled={item.status?.toLowerCase() === "available"}
          onClick={() => openDialog(item, "check-in")}
          sx={{
            backgroundColor: "#e8f5e9", color: "#2e7d32",
            textTransform: "none", borderRadius: "6px", fontSize: "12px",
            "&:disabled": { opacity: 0.4 },
          }}
        >
          Check-In
        </Button>
        <Button
          size="small"
          startIcon={<LogoutIcon />}
          disabled={item.status?.toLowerCase() === "checked out"}
          onClick={() => openDialog(item, "check-out")}
          sx={{
            backgroundColor: "#fce4ec", color: "#c62828",
            textTransform: "none", borderRadius: "6px", fontSize: "12px",
            "&:disabled": { opacity: 0.4 },
          }}
        >
          Check-Out
        </Button>
      </Box>
    ),
  }));

  return (
    <Box sx={{ p: 3 }}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity} message={toast.message} />

      <Typography variant="body2" color="text.secondary" mb={1}>Pages / Inventory</Typography>

      {/* Stats row */}
      <Grid container spacing={2} mb={3}>
        {[
          { label: "Total Items",    value: items.length,                                          color: "#244C77" },
          { label: "Available",      value: items.filter(i => i.status?.toLowerCase() === "available").length,    color: "#4caf50" },
          { label: "Checked Out",    value: items.filter(i => i.status?.toLowerCase() === "checked out").length,  color: "#f44336" },
        ].map((stat, idx) => (
          <Grid item xs={4} key={idx}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: "12px", textAlign: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mb: 0.5 }}>
                <InventoryIcon sx={{ color: stat.color, fontSize: "20px" }} />
                <Typography sx={{ fontSize: "22px", fontWeight: "bold", color: stat.color }}>{stat.value}</Typography>
              </Box>
              <Typography sx={{ fontSize: "12px", color: "#6B7280" }}>{stat.label}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1 }}>
        <Typography variant="h5" fontWeight="bold">Inventory</Typography>
        <TextField
          size="small"
          placeholder="Search items…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ width: 240, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
        />
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress sx={{ color: "#244C77" }} />
        </Box>
      ) : (
        <DynamicTable columns={columns} rows={rows} />
      )}

      {/* Check-In / Check-Out Dialog */}
      <Dialog open={dialog.open} onClose={() => setDialog({ open: false, item: null, action: "" })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: "bold" }}>
          {dialog.action === "check-in" ? "Check-In Item" : "Check-Out Item"}
        </DialogTitle>
        <DialogContent>
          {dialog.item && (
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: "14px", color: "#374151" }}>
                <strong>Item:</strong> {dialog.item.itemName} ({dialog.item.itemCode})
              </Typography>
              <Typography sx={{ fontSize: "13px", color: "#6B7280", mt: 0.5 }}>
                <strong>Location:</strong> {dialog.item.location}
              </Typography>
            </Box>
          )}
          <TextField
            label="Note (optional)"
            multiline rows={2}
            fullWidth size="small"
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Add a note or reason…"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialog({ open: false, item: null, action: "" })}
            sx={{ color: "#666", textTransform: "none", border: "1px solid #D5D7DA", borderRadius: "8px", px: 3 }}>
            Cancel
          </Button>
          <Button onClick={handleAction} disabled={saving}
            sx={{
              background: dialog.action === "check-in"
                ? "linear-gradient(135deg, #4caf50, #2e7d32)"
                : "linear-gradient(135deg, #ef5350, #c62828)",
              color: "#fff", textTransform: "none", borderRadius: "8px", px: 3,
            }}>
            {saving ? "Saving…" : dialog.action === "check-in" ? "Confirm Check-In" : "Confirm Check-Out"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StaffInventory;
